import React, { useEffect, useState } from "react";
import { apiGet, apiPut, apiPost, apiDelete } from "../../utils/api";
import {
  Card, CardBody, PageHeader, Badge, Spinner,
  TableWrapper, Table, Th, Td,
} from "../../components/ui";
import {
  FaUser, FaEdit, FaTimes, FaCheck, FaCrown, FaSearch, FaTrash,
  FaSync, FaKey, FaShieldAlt, FaEnvelope, FaEye, FaEyeSlash,
  FaWhatsapp, FaUserSecret, FaFileExport, FaCloud,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { CLOUD_API_FEATURE_KEY, QR_SESSIONS_FEATURE_KEY } from "../../utils/featureEntitlements";

const TABS = [
  { key: "profile", label: "Profile", icon: FaUser },
  { key: "subscription", label: "Subscription", icon: FaCrown },
  { key: "password", label: "Password", icon: FaKey },
  { key: "danger", label: "Danger Zone", icon: FaTrash },
];

const shortId = (id) => (id ? `${String(id).slice(0, 8)}...` : "-");

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const primaryWorkspaceFor = (user) => (
  user.activeWorkspace || user.defaultWorkspace || user.workspaces?.[0] || null
);

const CONNECTION_FEATURES = [
  { key: QR_SESSIONS_FEATURE_KEY, label: "QR / Web sessions", icon: FaWhatsapp },
  { key: CLOUD_API_FEATURE_KEY, label: "Cloud API sessions", icon: FaCloud },
];

const usageEntryFor = (usage = [], key) => {
  const direct = usage.find((item) => item.key === key);
  if (direct) return direct;
  if (key === QR_SESSIONS_FEATURE_KEY) return usage.find((item) => item.key === "whatsappConnection") || null;
  return null;
};

const isUsageEnabled = (entry) => !!entry && (entry.total > 0 || entry.total === -1);

export default function ManageUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState(null);

  // Unified modal state
  const [modalUser, setModalUser] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");

  // Profile form
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "user", isVerified: false });
  const [saving, setSaving] = useState(false);

  // Subscription
  const [currentSub, setCurrentSub] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [featureSaving, setFeatureSaving] = useState("");

  // Password
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [resettingPw, setResettingPw] = useState(false);

  // Delete
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // Create User
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({ email: "", password: "", name: "", role: "user", planKey: "" });
  const [creating, setCreating] = useState(false);

  const handleCreateUser = async () => {
    if (!createForm.email || !createForm.password) return showToast("Email and password required", "error");
    if (createForm.password.length < 8) return showToast("Password must be at least 8 characters", "error");
    try {
      setCreating(true);
      const res = await apiPost("/api/admin/users", createForm);
      if (res?.success) {
        showToast(`User ${createForm.email} created successfully`);
        setShowCreateForm(false);
        setCreateForm({ email: "", password: "", name: "", role: "user", planKey: "" });
        load();
      } else {
        showToast(res?.message || "Failed to create user", "error");
      }
    } catch (err) {
      showToast(err?.message || "Failed to create user", "error");
    } finally {
      setCreating(false);
    }
  };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = async () => {
    try {
      const [usersRes, plansRes] = await Promise.all([
        apiGet("/api/admin/users"),
        apiGet("/api/plans"),
      ]);
      setUsers(usersRes?.data || []);
      setPlans(plansRes?.plans || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Impersonate a user
  const handleImpersonate = async (user) => {
    try {
      const res = await apiPost(`/api/admin/users/${user.id}/impersonate`);
      if (res?.success) {
        // Store admin token for later restoration
        sessionStorage.setItem("adminToken", res.adminToken);
        sessionStorage.setItem("impersonatingUser", JSON.stringify({
          id: res.user.id,
          email: res.user.email,
          name: res.user.name,
        }));
        // Update localStorage role so ProtectedRoute works
        localStorage.setItem("role", res.user.role || "user");
        navigate("/dashboard");
        window.location.reload();
      }
    } catch (err) {
      setToast({ message: err?.message || "Failed to impersonate user", type: "error" });
    }
  };

  // Open unified modal
  const openModal = async (user) => {
    setModalUser(user);
    setActiveTab("profile");
    setForm({ name: user.name || "", email: user.email || "", phone: user.phone || "", role: user.role || "user", isVerified: !!user.isVerified });
    setNewPassword("");
    setShowPassword(false);
    setDeleteConfirmText("");
    setSelectedPlan("");
    setCurrentSub(null);

    // Fetch subscription
    try {
      const res = await apiGet(`/api/admin/users/${user.id}/subscription`);
      if (res?.success) setCurrentSub(res.subscription);
    } catch {
      setCurrentSub(null);
    }
  };

  const closeModal = () => {
    setModalUser(null);
  };

  // Profile save
  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await apiPut(`/api/admin/users/${modalUser.id}`, form);
      if (res?.success) {
        showToast("User updated successfully");
        load();
        setModalUser({ ...modalUser, ...form });
      } else {
        showToast(res?.message || "Failed to update", "error");
      }
    } catch {
      showToast("Failed to update user", "error");
    } finally {
      setSaving(false);
    }
  };

  // Assign plan
  const assignPlan = async () => {
    if (!selectedPlan) return;
    setAssigning(true);
    try {
      const res = await apiPost(`/api/admin/users/${modalUser.id}/assign-plan`, { planKey: selectedPlan });
      if (res?.success) {
        setCurrentSub(res.subscription);
        showToast("Plan assigned successfully");
        load();
      } else {
        showToast("Failed to assign plan", "error");
      }
    } catch {
      showToast("Error assigning plan", "error");
    } finally {
      setAssigning(false);
    }
  };

  // Reset usage
  const handleResetUsage = async () => {
    setResetting(true);
    try {
      const res = await apiPost(`/api/admin/users/${modalUser.id}/reset-usage`);
      if (res?.success) {
        setCurrentSub(res.subscription);
        showToast("Usage reset successfully");
      } else {
        showToast(res?.message || "Failed to reset usage", "error");
      }
    } catch {
      showToast("Failed to reset usage", "error");
    } finally {
      setResetting(false);
    }
  };

  const updateFeatureOverride = async (featureKey, enabled) => {
    if (!modalUser) return;
    setFeatureSaving(featureKey);
    try {
      const res = await apiPut(`/api/admin/users/${modalUser.id}/feature-overrides`, { [featureKey]: enabled });
      if (res?.success) {
        setCurrentSub(res.subscription);
        showToast("Feature access updated");
        load();
      } else {
        showToast(res?.message || "Failed to update feature access", "error");
      }
    } catch {
      showToast("Failed to update feature access", "error");
    } finally {
      setFeatureSaving("");
    }
  };

  // Reset password
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      showToast("Password must be at least 6 characters", "error");
      return;
    }
    setResettingPw(true);
    try {
      const res = await apiPost(`/api/admin/users/${modalUser.id}/reset-password`, { newPassword });
      if (res?.success) {
        showToast("Password reset successfully");
        setNewPassword("");
      } else {
        showToast(res?.message || "Failed to reset password", "error");
      }
    } catch {
      showToast("Failed to reset password", "error");
    } finally {
      setResettingPw(false);
    }
  };

  // Delete user
  const handleDelete = async () => {
    if (deleteConfirmText !== modalUser.email) return;
    setDeleting(true);
    try {
      const res = await apiDelete(`/api/admin/users/${modalUser.id}`);
      if (res?.success) {
        showToast("User deleted successfully");
        closeModal();
        load();
      } else {
        showToast(res?.message || "Failed to delete user", "error");
      }
    } catch {
      showToast("Failed to delete user", "error");
    } finally {
      setDeleting(false);
    }
  };

  const filtered = users.filter((u) =>
    (u.email || "").toLowerCase().includes(search.toLowerCase()) ||
    (u.name || "").toLowerCase().includes(search.toLowerCase())
  );

  const exportUsers = (format = "csv") => {
    const rows = filtered.map((u) => {
      const primaryWorkspace = primaryWorkspaceFor(u);
      return {
        "User ID": u.id || "",
        Name: u.name || "",
        Email: u.email || "",
        Phone: u.phone || "",
        Role: u.role || "user",
        Verified: u.isVerified ? "Yes" : "No",
        Plan: u.subscription?.planKey || "None",
        "Plan Status": u.subscription?.status || "",
        "Subscription Workspace ID": u.subscription?.workspaceId || "",
        "Default Workspace ID": u.defaultWorkspaceId || "",
        "Active Workspace ID": u.activeWorkspaceId || "",
        "Primary Workspace": primaryWorkspace?.name || "",
        "Primary Workspace ID": primaryWorkspace?.id || "",
        "Workspace Count": u.workspaces?.length || 0,
        Workspaces: (u.workspaces || []).map((w) => `${w.name || "Workspace"} (${w.id}) ${w.role || ""}`).join("; "),
        "Workspace Joined": primaryWorkspace?.joinedAt ? formatDateTime(primaryWorkspace.joinedAt) : "",
        "WhatsApp Sessions": (u.sessions || []).map((s) => s.number || s.sessionId).join("; "),
        "Messages Sent": u.messagesSent || 0,
        "Signed Up": u.createdAt ? formatDateTime(u.createdAt) : "",
        "Updated At": u.updatedAt ? formatDateTime(u.updatedAt) : "",
      };
    });

    if (format === "csv") {
      const headers = Object.keys(rows[0] || {});
      const csvContent = [
        headers.join(","),
        ...rows.map((r) =>
          headers.map((h) => {
            const val = String(r[h] ?? "").replace(/"/g, '""');
            return val.includes(",") || val.includes('"') || val.includes("\n") ? `"${val}"` : val;
          }).join(",")
        ),
      ].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `walytic-users-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const blob = new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `walytic-users-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
    showToast(`Exported ${rows.length} users as ${format.toUpperCase()}`);
  };

  if (loading) {
    return (
      <div>
        <PageHeader title="Manage Users" subtitle="View, edit, and manage user accounts" />
        <div className="flex items-center justify-center min-h-[300px]"><Spinner size="lg" /></div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Manage Users" subtitle="View, edit, and manage user accounts">
        <div className="flex items-center gap-3">
          <div className="text-sm text-ink-muted">{users.length} total users</div>
          <div className="relative group">
            <button className="btn-secondary flex items-center gap-1.5 text-xs">
              <FaFileExport size={10} /> Export
            </button>
            <div className="absolute right-0 top-full mt-1 bg-surface-card border border-surface-border rounded-lg shadow-lg py-1 hidden group-hover:block z-10 min-w-[120px]">
              <button onClick={() => exportUsers("csv")} className="w-full text-left px-3 py-1.5 text-xs text-ink-primary hover:bg-surface-bg">Export CSV</button>
              <button onClick={() => exportUsers("json")} className="w-full text-left px-3 py-1.5 text-xs text-ink-primary hover:bg-surface-bg">Export JSON</button>
            </div>
          </div>
          <button onClick={() => setShowCreateForm(!showCreateForm)} className="btn-primary flex items-center gap-1.5 text-xs">
            <FaUser size={10} /> Create User
          </button>
        </div>
      </PageHeader>

      {/* Create User Form */}
      {showCreateForm && (
        <Card className="mb-4 p-4">
          <h3 className="text-sm font-semibold text-ink-primary mb-3">Create New User</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
            <div className="input-group">
              <label className="input-label">Email *</label>
              <input type="email" className="input" placeholder="user@example.com" value={createForm.email}
                onChange={(e) => setCreateForm(p => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="input-group">
              <label className="input-label">Password *</label>
              <input type="text" className="input" placeholder="Min 8 characters" value={createForm.password}
                onChange={(e) => setCreateForm(p => ({ ...p, password: e.target.value }))} />
            </div>
            <div className="input-group">
              <label className="input-label">Full Name</label>
              <input type="text" className="input" placeholder="John Doe" value={createForm.name}
                onChange={(e) => setCreateForm(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="input-group">
              <label className="input-label">Role</label>
              <select className="input" value={createForm.role} onChange={(e) => setCreateForm(p => ({ ...p, role: e.target.value }))}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="input-group">
              <label className="input-label">Assign Plan</label>
              <select className="input" value={createForm.planKey} onChange={(e) => setCreateForm(p => ({ ...p, planKey: e.target.value }))}>
                <option value="">No plan (assign later)</option>
                {plans.map(p => <option key={p.id} value={p.key}>{p.name} ({p.key})</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreateUser} disabled={creating} className="btn-primary flex items-center gap-1.5 text-xs">
              {creating ? <Spinner size="sm" /> : <FaCheck size={10} />}
              {creating ? "Creating..." : "Create User"}
            </button>
            <button onClick={() => setShowCreateForm(false)} className="btn-secondary text-xs">Cancel</button>
          </div>
        </Card>
      )}

      {/* Toast */}
      {toast && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 ${
          toast.type === "error"
            ? "bg-red-500/10 text-red-500 border border-red-500/20"
            : "bg-green-500/10 text-green-500 border border-green-500/20"
        }`}>
          {toast.type === "error" ? "!" : <FaCheck size={12} />} {toast.msg}
        </div>
      )}

      {/* Users Table */}
      <Card className="mb-5">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-surface-border">
          <FaSearch size={13} className="text-ink-faint flex-shrink-0" />
          <input
            className="w-full bg-transparent text-sm text-ink-primary placeholder:text-ink-faint outline-none border-none"
            placeholder="Search users by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <CardBody className="p-0">
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>User</Th>
                  <Th>Workspace</Th>
                  <Th>Joined</Th>
                  <Th>Plan</Th>
                  <Th>Sessions</Th>
                  <Th>Sent</Th>
                  <Th>Role</Th>
                  <Th>Verified</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const primaryWorkspace = primaryWorkspaceFor(u);
                  return (
                  <tr key={u.id} className="cursor-pointer hover:bg-surface-bg/50" onClick={() => openModal(u)}>
                    <Td>
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs text-white flex-shrink-0"
                          style={{ background: "linear-gradient(135deg, #2C86E9, #0155A8)" }}
                        >
                          {(u.name || u.email || "U").charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-ink-primary">{u.name || "-"}</p>
                          <p className="text-xs text-ink-muted">{u.email}</p>
                          <p className="text-[11px] text-ink-faint font-mono" title={u.id}>ID {shortId(u.id)}</p>
                        </div>
                      </div>
                    </Td>
                    <Td>
                      {primaryWorkspace ? (
                        <div className="space-y-0.5">
                          <p className="text-xs font-medium text-ink-primary">{primaryWorkspace.name || "Workspace"}</p>
                          <p className="text-[11px] text-ink-faint font-mono" title={primaryWorkspace.id}>
                            {shortId(primaryWorkspace.id)}
                          </p>
                          <p className="text-[11px] text-ink-muted">
                            {primaryWorkspace.role || "member"} / {u.workspaces?.length || 1} workspace{(u.workspaces?.length || 1) === 1 ? "" : "s"}
                          </p>
                        </div>
                      ) : (
                        <span className="text-xs text-ink-faint">No workspace</span>
                      )}
                    </Td>
                    <Td>
                      <div className="space-y-0.5">
                        <p className="text-xs text-ink-primary">{formatDate(u.createdAt)}</p>
                        <p className="text-[11px] text-ink-muted">
                          Workspace {formatDate(primaryWorkspace?.joinedAt)}
                        </p>
                      </div>
                    </Td>
                    <Td>
                      {u.subscription ? (
                        u.subscription.isTrial ? (
                          <Badge variant="warning">
                            {u.subscription.planKey} trial
                            {typeof u.subscription.trialDaysLeft === "number" && ` · ${u.subscription.trialDaysLeft}d`}
                          </Badge>
                        ) : (
                          <Badge variant={u.subscription.status === "active" ? "success" : "warning"}>
                            {u.subscription.planKey}
                          </Badge>
                        )
                      ) : (
                        <span className="text-xs text-ink-faint">No plan</span>
                      )}
                    </Td>
                    <Td>
                      {u.sessions?.length > 0 ? (
                        <div className="space-y-1">
                          {u.sessions.map((s) => (
                            <div key={s.sessionId} className="flex items-center gap-1.5">
                              <FaWhatsapp size={11} className={s.status === "connected" ? "text-green-500" : "text-ink-faint"} />
                              <span className="text-xs text-ink-primary">{s.number || s.sessionId}</span>
                              <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.status === "connected" ? "bg-green-500" : "bg-ink-faint"}`} />
                              <span className="text-[11px] text-ink-faint">{formatDate(s.lastMessageTime || s.createdAt)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-ink-faint">None</span>
                      )}
                    </Td>
                    <Td>
                      <span className="text-xs font-medium text-ink-primary">{(u.messagesSent || 0).toLocaleString()}</span>
                    </Td>
                    <Td>
                      <Badge variant={u.role === "admin" ? "brand" : "neutral"}>
                        {u.role || "user"}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge variant={u.isVerified ? "success" : "warning"}>
                        {u.isVerified ? "Verified" : "Pending"}
                      </Badge>
                    </Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <button
                          className="btn-secondary btn-sm flex items-center gap-1.5"
                          onClick={(e) => { e.stopPropagation(); openModal(u); }}
                        >
                          <FaEdit size={11} /> Manage
                        </button>
                        {u.role !== "admin" && (
                          <button
                            className="btn-secondary btn-sm flex items-center gap-1.5 text-amber-600 hover:text-amber-700"
                            onClick={(e) => { e.stopPropagation(); handleImpersonate(u); }}
                            title="Impersonate this user"
                          >
                            <FaUserSecret size={11} />
                          </button>
                        )}
                      </div>
                    </Td>
                  </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <Td colSpan="9" className="text-center text-ink-muted py-8">
                      {search ? "No users match your search" : "No users found"}
                    </Td>
                  </tr>
                )}
              </tbody>
            </Table>
          </TableWrapper>
        </CardBody>
      </Card>

      {/* ═══════════════════════════════════════════════
          UNIFIED USER MANAGEMENT MODAL
          ═══════════════════════════════════════════════ */}
      {modalUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={closeModal}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <Card className="flex flex-col max-h-[90vh]">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm text-white flex-shrink-0"
                    style={{ background: "linear-gradient(135deg, #2C86E9, #0155A8)" }}
                  >
                    {(modalUser.name || modalUser.email || "U").charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-ink-primary">{modalUser.name || "Unnamed User"}</p>
                    <p className="text-xs text-ink-muted">{modalUser.email}</p>
                  </div>
                </div>
                <button className="text-ink-muted hover:text-ink-primary p-1" onClick={closeModal}>
                  <FaTimes size={16} />
                </button>
              </div>

              {/* Tab Navigation */}
              <div className="flex border-b border-surface-border px-5 flex-shrink-0">
                {TABS.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                        isActive
                          ? "border-brand text-brand"
                          : "border-transparent text-ink-muted hover:text-ink-primary"
                      } ${tab.key === "danger" ? "ml-auto text-red-500" : ""}`}
                    >
                      <Icon size={11} />
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto px-5 py-4">

                {/* ── Profile Tab ── */}
                {activeTab === "profile" && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-lg border border-surface-border bg-surface-bg">
                      <div>
                        <p className="text-[11px] uppercase text-ink-faint">User ID</p>
                        <p className="text-xs font-mono text-ink-primary break-all">{modalUser.id}</p>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase text-ink-faint">Signed Up</p>
                        <p className="text-xs text-ink-primary">{formatDateTime(modalUser.createdAt)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase text-ink-faint">Updated</p>
                        <p className="text-xs text-ink-primary">{formatDateTime(modalUser.updatedAt)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase text-ink-faint">Workspace Joined</p>
                        <p className="text-xs text-ink-primary">{formatDateTime(primaryWorkspaceFor(modalUser)?.joinedAt)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase text-ink-faint">Active Workspace ID</p>
                        <p className="text-xs font-mono text-ink-primary break-all">{modalUser.activeWorkspaceId || "-"}</p>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase text-ink-faint">Default Workspace ID</p>
                        <p className="text-xs font-mono text-ink-primary break-all">{modalUser.defaultWorkspaceId || "-"}</p>
                      </div>
                    </div>
                    {modalUser.workspaces?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-ink-secondary">Workspace Memberships</p>
                        {modalUser.workspaces.map((workspace) => (
                          <div key={workspace.id} className="flex items-start justify-between gap-3 text-xs border border-surface-border rounded-lg px-3 py-2">
                            <div className="min-w-0">
                              <p className="font-medium text-ink-primary truncate">{workspace.name || "Workspace"}</p>
                              <p className="font-mono text-ink-faint break-all">{workspace.id}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <Badge variant={workspace.role === "owner" || workspace.role === "admin" ? "brand" : "neutral"}>
                                {workspace.role || "member"}
                              </Badge>
                              <p className="mt-1 text-ink-muted">{formatDate(workspace.joinedAt)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="input-group">
                      <label className="input-label">Name</label>
                      <input
                        className="input"
                        placeholder="User name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                      />
                    </div>
                    <div className="input-group">
                      <label className="input-label">Email</label>
                      <input
                        type="email"
                        className="input"
                        placeholder="user@example.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                      />
                      <p className="input-hint">Admin override — updates the user's login email. Must be unique.</p>
                    </div>
                    <div className="input-group">
                      <label className="input-label">WhatsApp Number</label>
                      <input
                        className="input"
                        placeholder="e.g. 919876543210"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      />
                      <p className="input-hint">Country code + number, no + or spaces</p>
                    </div>
                    <div className="input-group">
                      <label className="input-label">Role</label>
                      <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <div className="input-group">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="accent-brand"
                          checked={form.isVerified}
                          onChange={(e) => setForm({ ...form, isVerified: e.target.checked })}
                        />
                        <span className="text-sm text-ink-primary">Email Verified</span>
                      </label>
                    </div>
                    <button className="btn-primary flex items-center gap-2" onClick={saveProfile} disabled={saving}>
                      {saving ? <Spinner size="sm" /> : <FaCheck size={11} />}
                      {saving ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                )}

                {/* ── Subscription Tab ── */}
                {activeTab === "subscription" && (
                  <div className="space-y-4">
                    {/* Current subscription */}
                    {currentSub ? (
                      <div className="p-3 rounded-lg border border-surface-border bg-surface-bg space-y-3">
                        <div className="flex items-center gap-3 text-sm flex-wrap">
                          <span className="font-semibold text-ink-primary">{currentSub.planKey}</span>
                          <Badge variant={currentSub.status === "active" ? "success" : "warning"}>
                            {currentSub.status}
                          </Badge>
                          {currentSub.interval && <Badge variant="neutral">{currentSub.interval}</Badge>}
                        </div>
                        <p className="text-xs text-ink-muted">
                          {currentSub.interval === 'lifetime'
                            ? `Since ${currentSub.currentPeriodStart ? new Date(currentSub.currentPeriodStart).toLocaleDateString() : '—'} — Lifetime Access`
                            : `${currentSub.currentPeriodStart ? new Date(currentSub.currentPeriodStart).toLocaleDateString() : '—'} — ${currentSub.currentPeriodEnd ? new Date(currentSub.currentPeriodEnd).toLocaleDateString() : '—'}`
                          }
                        </p>

                        {/* Usage */}
                        {currentSub.usage?.length > 0 && (
                          <div className="space-y-2 pt-2 border-t border-surface-border">
                            <p className="text-xs font-medium text-ink-secondary">Usage This Cycle</p>
                            {currentSub.usage.map((u) => {
                              const pct = u.total === -1 ? 0 : u.total > 0 ? Math.min(100, Math.round((u.used / u.total) * 100)) : 0;
                              const isOver = u.total !== -1 && u.used >= u.total;
                              return (
                                <div key={u.key}>
                                  <div className="flex items-center justify-between text-xs mb-1">
                                    <span className="font-medium text-ink-primary">{u.key}</span>
                                    <span className={isOver ? "text-red-500 font-semibold" : "text-ink-muted"}>
                                      {u.used} / {u.total === -1 ? "Unlimited" : u.total}
                                    </span>
                                  </div>
                                  {u.total !== -1 && (
                                    <div className="w-full h-1.5 rounded-full bg-surface-border overflow-hidden">
                                      <div
                                        className={`h-full rounded-full transition-all ${isOver ? "bg-red-500" : pct > 80 ? "bg-yellow-500" : "bg-brand"}`}
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        <div className="space-y-2 pt-2 border-t border-surface-border">
                          <p className="text-xs font-medium text-ink-secondary">Connection Access</p>
                          {CONNECTION_FEATURES.map((feature) => {
                            const Icon = feature.icon;
                            const entry = usageEntryFor(currentSub.usage || [], feature.key);
                            const enabled = isUsageEnabled(entry);
                            const savingThis = featureSaving === feature.key;
                            return (
                              <div key={feature.key} className="flex items-center justify-between gap-3 rounded-lg border border-surface-border px-3 py-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <Icon size={12} className="text-ink-muted flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-xs font-medium text-ink-primary">{feature.label}</p>
                                    <p className="text-[11px] text-ink-faint">{entry?.override ? "Admin override" : "Plan default"}</p>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors ${enabled ? "bg-brand" : "bg-surface-border"}`}
                                  disabled={!!featureSaving}
                                  onClick={() => updateFeatureOverride(feature.key, !enabled)}
                                  title={enabled ? "Disable" : "Enable"}
                                >
                                  <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${enabled ? "translate-x-4" : "translate-x-0.5"}`} />
                                  {savingThis && <span className="absolute inset-0 rounded-full bg-black/10" />}
                                </button>
                              </div>
                            );
                          })}
                        </div>

                        {currentSub.status === "active" && (
                          <button
                            className="btn-secondary btn-sm w-full flex items-center justify-center gap-2 text-amber-500"
                            onClick={handleResetUsage}
                            disabled={resetting}
                          >
                            {resetting ? <Spinner size="sm" /> : <FaSync size={11} />}
                            {resetting ? "Resetting..." : "Reset All Usage"}
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="p-4 rounded-lg border border-dashed border-surface-border text-center">
                        <p className="text-sm text-ink-muted">No active subscription</p>
                      </div>
                    )}

                    {/* Assign plan */}
                    <div className="input-group">
                      <label className="input-label">Assign Plan</label>
                      <select className="input" value={selectedPlan} onChange={(e) => setSelectedPlan(e.target.value)}>
                        <option value="">-- Choose a plan --</option>
                        {plans.map((p) => (
                          <option key={p.id} value={p.key}>{p.name} ({p.key})</option>
                        ))}
                      </select>
                    </div>
                    <button className="btn-primary flex items-center gap-2" onClick={assignPlan} disabled={assigning || !selectedPlan}>
                      {assigning ? <Spinner size="sm" /> : <FaCrown size={11} />}
                      {assigning ? "Assigning..." : "Assign Plan"}
                    </button>
                  </div>
                )}

                {/* ── Password Tab ── */}
                {activeTab === "password" && (
                  <div className="space-y-4">
                    <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
                      Set a new password for this user. They will need to use this password on their next login.
                    </div>
                    <div className="input-group">
                      <label className="input-label">New Password</label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          className="input pr-10"
                          placeholder="Enter new password (min 6 chars)"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink-primary"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                        </button>
                      </div>
                    </div>
                    <button
                      className="btn-primary flex items-center gap-2"
                      onClick={handleResetPassword}
                      disabled={resettingPw || !newPassword || newPassword.length < 6}
                    >
                      {resettingPw ? <Spinner size="sm" /> : <FaKey size={11} />}
                      {resettingPw ? "Resetting..." : "Reset Password"}
                    </button>
                  </div>
                )}

                {/* ── Danger Zone Tab ── */}
                {activeTab === "danger" && (
                  <div className="space-y-4">
                    {modalUser.role === "admin" ? (
                      <div className="p-4 rounded-lg border border-surface-border text-center">
                        <FaShieldAlt size={20} className="mx-auto text-ink-faint mb-2" />
                        <p className="text-sm text-ink-muted">Admin accounts cannot be deleted from here.</p>
                      </div>
                    ) : (
                      <>
                        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 space-y-1">
                          <p className="font-semibold">This action is permanent and cannot be undone.</p>
                          <p>Deleting this user will remove their account, subscription, WhatsApp sessions, team memberships, and all associated data.</p>
                        </div>
                        <div className="input-group">
                          <label className="input-label">Type the user's email to confirm</label>
                          <input
                            className="input"
                            placeholder={modalUser.email}
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                          />
                        </div>
                        <button
                          className="btn-danger w-full flex items-center justify-center gap-2"
                          onClick={handleDelete}
                          disabled={deleting || deleteConfirmText !== modalUser.email}
                        >
                          {deleting ? <Spinner size="sm" /> : <FaTrash size={11} />}
                          {deleting ? "Deleting..." : "Permanently Delete User"}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
